import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@cca/ui';
import { AlertTriangle } from 'lucide-react';
import { ApiError, apiRequest } from '../lib/api.js';
import { useAccessToken } from '../lib/session.js';
import { RequireSession } from '../components/require-session.js';
import { rootRoute } from './root.js';

interface AutomationRule {
  readonly id: string;
  readonly template_key: string | null;
  readonly name: string;
  readonly description: string;
  readonly trigger: string;
  readonly action: string;
  readonly autonomy_level: string;
  readonly is_enabled: boolean;
  readonly max_recipients_per_run: number;
  readonly last_run_at: string | null;
  readonly last_run_status: string | null;
  readonly last_error: string | null;
}

interface AutomationRun {
  readonly id: string;
  readonly rule_id: string;
  readonly status: string;
  readonly refusal_reason: string | null;
  readonly detail: string | null;
  readonly candidates_found: number;
  readonly actions_created: number;
  readonly started_at: string;
}

interface JobFailure {
  readonly id: string;
  readonly queue: string;
  readonly job_kind: string;
  readonly status: string;
  readonly attempts: number;
  readonly max_attempts: number;
  readonly error_code: string | null;
  readonly error_message: string | null;
  readonly last_failed_at: string;
}

const AUTONOMY_TONE: Record<string, 'neutral' | 'info' | 'warning'> = {
  DRAFT_ONLY: 'neutral',
  APPROVAL_REQUIRED: 'info',
  AUTO_EXECUTE: 'warning',
};

function humanise(value: string): string {
  return value.replaceAll('_', ' ').toLowerCase();
}

function RuleCard({
  rule,
  accessToken,
  onChanged,
}: {
  readonly rule: AutomationRule;
  readonly accessToken: string | null;
  readonly onChanged: () => void;
}) {
  const toggle = useMutation({
    mutationFn: (isEnabled: boolean) =>
      apiRequest<{ isEnabled: boolean }>(`/api/automations/${rule.id}/toggle`, {
        method: 'POST',
        accessToken,
        body: { isEnabled },
      }),
    onSuccess: onChanged,
  });

  // The description carries the template's safety note after a blank line.
  const [summary, safety] = rule.description.split('\n\nSafety: ');

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>{rule.name}</CardTitle>
          <CardDescription>
            {humanise(rule.trigger)} → {humanise(rule.action)}
          </CardDescription>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge tone={rule.is_enabled ? 'success' : 'neutral'}>
            {rule.is_enabled ? 'enabled' : 'disabled'}
          </Badge>
          <Badge tone={AUTONOMY_TONE[rule.autonomy_level] ?? 'neutral'}>
            {humanise(rule.autonomy_level)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-sm text-slate-700 dark:text-slate-300">{summary}</p>

        {safety ? (
          <p className="rounded-md bg-slate-50 p-3 text-xs text-slate-600 dark:bg-slate-950 dark:text-slate-400">
            <span className="font-medium text-slate-700 dark:text-slate-300">Safety: </span>
            {safety}
          </p>
        ) : null}

        <p className="text-xs text-slate-500 dark:text-slate-400">
          At most {rule.max_recipients_per_run} per run
          {rule.last_run_at
            ? ` · last run ${new Date(rule.last_run_at).toLocaleString()} (${humanise(rule.last_run_status ?? 'unknown')})`
            : ' · never run'}
        </p>

        {rule.last_error ? (
          <p role="alert" className="text-xs text-red-700 dark:text-red-400">
            {rule.last_error}
          </p>
        ) : null}

        <Button
          size="sm"
          variant={rule.is_enabled ? 'outline' : 'primary'}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(!rule.is_enabled)}
        >
          {toggle.isPending ? 'Saving…' : rule.is_enabled ? 'Disable' : 'Enable'}
        </Button>

        {toggle.isError ? (
          <p role="alert" className="text-sm text-red-700 dark:text-red-400">
            {toggle.error instanceof ApiError ? toggle.error.message : 'Could not change this.'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AutomationsPage() {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const automations = useQuery({
    queryKey: ['automations'],
    enabled: Boolean(accessToken),
    queryFn: () =>
      apiRequest<{ rules: AutomationRule[]; automationExecutionEnabled: boolean }>(
        '/api/automations',
        { accessToken },
      ),
  });

  const runs = useQuery({
    queryKey: ['automation-runs'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<{ runs: AutomationRun[] }>('/api/automations/runs', { accessToken }),
  });

  const failures = useQuery({
    queryKey: ['job-failures'],
    enabled: Boolean(accessToken),
    queryFn: () => apiRequest<{ failures: JobFailure[] }>('/api/jobs/failures', { accessToken }),
  });

  const install = useMutation({
    mutationFn: () =>
      apiRequest<{ installed: number; available: number }>('/api/automations/install-templates', {
        method: 'POST',
        accessToken,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['automations'] }),
  });

  const resolve = useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ status: string }>(`/api/jobs/failures/${id}/resolve`, {
        method: 'POST',
        accessToken,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['job-failures'] }),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['automations'] });
    void queryClient.invalidateQueries({ queryKey: ['automation-runs'] });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Automations</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Rules that act when nobody is watching. Every one ships disabled.
          </p>
        </div>
        <Button variant="outline" disabled={install.isPending} onClick={() => install.mutate()}>
          {install.isPending ? 'Installing…' : 'Install shipped templates'}
        </Button>
      </header>

      {automations.data && !automations.data.automationExecutionEnabled ? (
        <div
          role="status"
          className="flex gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <strong className="font-semibold">
              AUTOMATION_EXECUTION_ENABLED is off for this environment.
            </strong>{' '}
            An enabled rule below will still be refused at evaluation time, and the refusal is
            recorded.
          </span>
        </div>
      ) : null}

      {install.isSuccess ? (
        <p role="status" className="text-sm text-emerald-800 dark:text-emerald-300">
          Installed {install.data.installed} of {install.data.available} templates — all disabled.
        </p>
      ) : null}

      {automations.isPending ? <LoadingState label="Loading rules" /> : null}
      {automations.isError ? (
        <ErrorState
          description={
            automations.error instanceof ApiError
              ? automations.error.message
              : 'Could not load automations.'
          }
          onRetry={() => void automations.refetch()}
        />
      ) : null}

      {automations.data && automations.data.rules.length === 0 ? (
        <Card>
          <EmptyState
            title="No automations installed"
            description="Install the shipped templates to see the eight standard rules. They arrive disabled."
          />
        </Card>
      ) : null}

      <ul className="space-y-3">
        {(automations.data?.rules ?? []).map((rule) => (
          <li key={rule.id}>
            <RuleCard rule={rule} accessToken={accessToken} onChanged={refresh} />
          </li>
        ))}
      </ul>

      <section aria-label="Recent runs">
        <h2 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          Recent runs
        </h2>
        <Card>
          <CardContent>
            {(runs.data?.runs ?? []).length === 0 ? (
              <p className="text-sm text-slate-600 dark:text-slate-400">
                No runs recorded. Runs that do nothing are recorded too, so an empty list means the
                scheduler has not fired yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {(runs.data?.runs ?? []).map((run) => (
                  <li key={run.id} className="flex flex-wrap items-baseline gap-2 text-xs">
                    <Badge
                      tone={
                        run.status === 'RAN'
                          ? 'success'
                          : run.status === 'REFUSED'
                            ? 'warning'
                            : 'danger'
                      }
                    >
                      {run.status.toLowerCase()}
                    </Badge>
                    <span className="text-slate-600 dark:text-slate-400">
                      {run.candidates_found} found, {run.actions_created} created
                    </span>
                    {run.refusal_reason ? (
                      <span className="text-amber-700 dark:text-amber-300">
                        {humanise(run.refusal_reason)}
                      </span>
                    ) : null}
                    <span className="text-slate-400">
                      {new Date(run.started_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      <section aria-label="Failed jobs">
        <h2 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          Failed background jobs
        </h2>
        <Card>
          <CardContent>
            {(failures.data?.failures ?? []).length === 0 ? (
              <p className="text-sm text-slate-600 dark:text-slate-400">Nothing is stuck.</p>
            ) : (
              <ul className="space-y-2">
                {(failures.data?.failures ?? []).map((failure) => (
                  <li
                    key={failure.id}
                    className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2 text-xs last:border-0 dark:border-slate-800"
                  >
                    <div>
                      <Badge tone={failure.status === 'DEAD_LETTERED' ? 'danger' : 'warning'}>
                        {humanise(failure.status)}
                      </Badge>
                      <span className="ml-2 text-slate-700 dark:text-slate-300">
                        {failure.queue} · {failure.job_kind}
                      </span>
                      <span className="ml-2 text-slate-500 dark:text-slate-400">
                        attempt {failure.attempts} of {failure.max_attempts}
                      </span>
                      {failure.error_message ? (
                        <p className="mt-0.5 text-slate-500 dark:text-slate-400">
                          {failure.error_message}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={resolve.isPending}
                      onClick={() => resolve.mutate(failure.id)}
                    >
                      Mark resolved
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

export const automationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/automations',
  component: () => (
    <RequireSession>
      <AutomationsPage />
    </RequireSession>
  ),
});
